import { useState, forwardRef, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuery } from '@tanstack/react-query';
import { InviteModal } from '@/components/InviteModal';
import { useInviteToken } from '@/hooks/useInviteToken';
import { calculateDaysRemaining } from '@/lib/utils';
import { toast } from 'sonner';

interface LeaderboardEntry {
  user_id: string;
  profile_user_id: string | null;
  beer_count: number;
  wine_count: number;
  shot_count: number;
  total: number;
  longest_streak: number;
  rank?: number;
}

interface LeaderboardSectionProps {
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

// Preview rows for empty/no-over-limit groups
const PREVIEW_ROW_NORMAL: LeaderboardEntry = {
  user_id: 'preview-normal',
  profile_user_id: 'tvoj_kamos',
  beer_count: 1, wine_count: 0, shot_count: 0,
  total: 1, longest_streak: 0,
};

const PREVIEW_ROW_OVER: LeaderboardEntry = {
  user_id: 'preview-over',
  profile_user_id: 'viac_opity_kamos',
  beer_count: 80, wine_count: 15, shot_count: 6,
  total: 101, longest_streak: 0,
};

function LeaderboardRow({ 
  entry, rank, isCurrentUser, t 
}: { 
  entry: LeaderboardEntry; 
  rank: number; 
  isCurrentUser: boolean;
  t: (key: string) => string;
}) {
  const displayName = entry.profile_user_id || 'Anonymous';
  const isOverLimit = entry.total > 100;
  const isPreview = entry.user_id.startsWith('preview-');
  
  return (
    <div 
      className={`grid grid-cols-[minmax(0,1fr)_40px_36px_28px_24px_24px] gap-1.5 py-2 rounded-md px-1 ${
        isCurrentUser ? 'bg-primary/5' : ''
      } ${isOverLimit ? 'bg-destructive/5' : ''} ${isPreview ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center gap-1 min-w-0">
        <span className={`text-xs sm:text-sm font-medium w-5 shrink-0 tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>
          {rank}.
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`text-xs sm:text-sm font-medium truncate ${isOverLimit ? 'text-destructive' : ''}`}>
              {displayName}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[200px]">
            <p className="break-words">{displayName}</p>
          </TooltipContent>
        </Tooltip>
        {isCurrentUser && (
          <span className="text-[10px] text-muted-foreground shrink-0 ml-0.5">{t('leaderboardYou')}</span>
        )}
      </div>
      <span className={`text-center text-xs sm:text-sm font-bold tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.total}</span>
      <span className={`text-center text-xs sm:text-sm tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.longest_streak}</span>
      <span className={`text-center text-xs sm:text-sm tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.beer_count}</span>
      <span className={`text-center text-xs sm:text-sm tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.wine_count}</span>
      <span className={`text-center text-xs sm:text-sm tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.shot_count}</span>
    </div>
  );
}

export const LeaderboardSection = forwardRef<HTMLElement, LeaderboardSectionProps>(
  function LeaderboardSection({ onModalOpen, onModalClose }, ref) {
    const { group, user } = useAuth();
    const { t } = useLanguage();
    const [showInviteModal, setShowInviteModal] = useState(false);
    const { token: inviteToken } = useInviteToken();

    useEffect(() => {
      if (showInviteModal) {
        onModalOpen?.();
      } else {
        onModalClose?.();
      }
    }, [showInviteModal, onModalOpen, onModalClose]);

    const { data: leaderboard, isLoading } = useQuery({
      queryKey: ['leaderboard', group?.id],
      queryFn: async () => {
        if (!group) return [];

        const { data: memberships } = await supabase
          .from('memberships')
          .select('user_id')
          .eq('group_id', group.id);

        if (!memberships) return [];

        const { data: profiles } = await supabase
          .rpc('get_group_member_profiles', { target_group_id: group.id });

        const { data: drinkLogs } = await supabase
          .from('drink_logs')
          .select('user_id, drink_type, logged_at')
          .eq('group_id', group.id);

        const calculateLongestStreak = (userId: string): number => {
          if (!group) return 0;
          const start = new Date(group.challenge_start);
          start.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const userLogs = (drinkLogs || []).filter(log => log.user_id === userId);
          if (userLogs.length === 0) {
            return Math.max(0, Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
          }
          const sortedLogs = [...userLogs].sort((a, b) => 
            new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime()
          );
          let longestStreak = 0;
          const firstDrink = new Date(sortedLogs[0].logged_at);
          firstDrink.setHours(0, 0, 0, 0);
          longestStreak = Math.max(longestStreak, Math.floor((firstDrink.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
          for (let i = 0; i < sortedLogs.length - 1; i++) {
            const current = new Date(sortedLogs[i].logged_at);
            current.setHours(0, 0, 0, 0);
            const next = new Date(sortedLogs[i + 1].logged_at);
            next.setHours(0, 0, 0, 0);
            const gap = Math.floor((next.getTime() - current.getTime()) / (1000 * 60 * 60 * 24)) - 1;
            longestStreak = Math.max(longestStreak, gap);
          }
          const lastDrink = new Date(sortedLogs[sortedLogs.length - 1].logged_at);
          lastDrink.setHours(0, 0, 0, 0);
          longestStreak = Math.max(longestStreak, Math.floor((today.getTime() - lastDrink.getTime()) / (1000 * 60 * 60 * 24)));
          return longestStreak;
        };

        const leaderboardMap = new Map<string, LeaderboardEntry>();
        for (const membership of memberships) {
          const profile = profiles?.find(p => p.id === membership.user_id);
          leaderboardMap.set(membership.user_id, {
            user_id: membership.user_id,
            profile_user_id: profile?.user_id || null,
            beer_count: 0, wine_count: 0, shot_count: 0,
            total: 0,
            longest_streak: calculateLongestStreak(membership.user_id),
          });
        }

        for (const log of drinkLogs || []) {
          const entry = leaderboardMap.get(log.user_id);
          if (entry) {
            if (log.drink_type === 'beer') entry.beer_count++;
            else if (log.drink_type === 'wine') entry.wine_count++;
            else if (log.drink_type === 'shot') entry.shot_count++;
            entry.total++;
          }
        }

        return Array.from(leaderboardMap.values()).sort((a, b) => {
          if (a.total !== b.total) return a.total - b.total;
          return b.longest_streak - a.longest_streak;
        });
      },
      enabled: !!group,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
      staleTime: 10000,
    });

    const daysRemaining = group ? calculateDaysRemaining(group.challenge_end) : 0;

    const memberCount = leaderboard?.length || 0;
    const isSingleMember = memberCount === 1;

    const hasStarted = useMemo(() => {
      if (!group) return false;
      const now = new Date();
      const start = new Date(group.challenge_start);
      return now >= start;
    }, [group]);

    // Compute global rank, then split into sections
    const { sectionA, sectionB } = useMemo(() => {
      if (!leaderboard) return { sectionA: [], sectionB: [] };
      const ranked = leaderboard.map((entry, index) => ({
        ...entry,
        rank: index + 1,
      }));
      const a = ranked.filter(e => e.total <= 100);
      const b = ranked.filter(e => e.total > 100);
      return { sectionA: a, sectionB: b };
    }, [leaderboard]);

    // Preview rows with sequential numbering
    const totalReal = (leaderboard?.length || 0);

    const previewRowsA = useMemo(() => {
      if (!group || !isSingleMember) return [];
      return [{ ...PREVIEW_ROW_NORMAL, rank: totalReal + 1 }];
    }, [group, isSingleMember, totalReal]);

    const previewRowsB = useMemo(() => {
      if (!group) return [];
      const realOverLimitCount = sectionB.length;
      const extraOffset = isSingleMember ? 1 : 0; // account for preview row in A
      
      if (isSingleMember && realOverLimitCount === 0) {
        return [{ ...PREVIEW_ROW_OVER, rank: totalReal + 1 + extraOffset }];
      }
      if (!isSingleMember && realOverLimitCount === 0) {
        return [{ ...PREVIEW_ROW_OVER, rank: totalReal + 1 }];
      }
      return [];
    }, [group, sectionB.length, isSingleMember, totalReal]);

    const displayCode = inviteToken || '···';

    const handleCopyInviteCode = () => {
      if (inviteToken) {
        const joinUrl = `https://tugedr.com/join?token=${inviteToken}`;
        navigator.clipboard.writeText(joinUrl);
        toast.success(t('inviteModalCopied'));
      }
    };

    return (
      <section ref={ref} id="leaderboard" className="py-8 border-t border-border">
        {/* Section Header */}
        <div className="px-6 pb-4 text-center max-w-lg mx-auto">
          <h2 className="text-xl font-serif font-bold mb-2">{t('leaderboardTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {daysRemaining > 0 ? `${daysRemaining} ${t('leaderboardDaysRemaining')}` : t('leaderboardComplete')}
          </p>
          {group && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('leaderboardInviteCode')}{' '}
              <button
                onClick={() => inviteToken && setShowInviteModal(true)}
                className="font-mono underline underline-offset-2 decoration-primary/50 hover:decoration-primary transition-colors"
              >
                {displayCode}
              </button>
            </p>
          )}
        </div>

        {/* Invite Modal */}
        {showInviteModal && inviteToken && (
          <InviteModal 
            token={inviteToken} 
            onClose={() => setShowInviteModal(false)} 
          />
        )}

        {/* Single-member tooltip */}
        {isSingleMember && group && (
          <div className="px-6 max-w-lg mx-auto mb-4">
            <div className="relative">
              <div 
                className="absolute left-1/2 -translate-x-1/2 -top-2 w-0 h-0"
                style={{
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderBottom: '10px solid hsl(var(--primary))',
                }}
              />
              <div className="bg-primary text-primary-foreground px-4 py-3 rounded-lg text-center shadow-lg">
                {!hasStarted ? (
                  <div className="space-y-1">
                    <p className="text-base font-semibold">🕛 {t('leaderboardTooltipHeadline')}</p>
                    <p className="text-sm">
                      {t('leaderboardTooltipBeforeMidnightBody')}{' '}
                      <button
                        onClick={() => inviteToken && setShowInviteModal(true)}
                        className="font-mono font-bold underline underline-offset-2 decoration-primary-foreground/50 hover:decoration-primary-foreground transition-colors"
                      >
                        {displayCode}
                      </button>
                      ?
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="text-base font-semibold">{t('leaderboardTooltipAfterHeadline')}</p>
                    <p className="text-sm">
                      {t('leaderboardTooltipAfterMidnightBody')}{' '}
                      <button
                        onClick={() => inviteToken && setShowInviteModal(true)}
                        className="font-mono font-bold underline underline-offset-2 decoration-primary-foreground/50 hover:decoration-primary-foreground transition-colors"
                      >
                        {displayCode}
                      </button>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard Table */}
        <div className="px-6 max-w-lg mx-auto">
          {isLoading ? (
            <div className="text-center text-muted-foreground">{t('loading')}</div>
          ) : leaderboard && leaderboard.length > 0 ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('leaderboardRankings')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-0.5">
                  {/* Header row */}
                  <div className="grid grid-cols-[minmax(0,1fr)_40px_36px_28px_24px_24px] gap-1.5 text-xs text-muted-foreground font-medium pb-1.5 border-b border-border/60 px-1">
                    <span>{t('leaderboardUser')}</span>
                    <span className="text-center font-bold">{t('leaderboardTotal')}</span>
                    <span className="text-center">🗓️</span>
                    <span className="text-center">🍺</span>
                    <span className="text-center">🍷</span>
                    <span className="text-center">🥃</span>
                  </div>

                  {/* Section A: Users with ≤100 drinks */}
                  {sectionA.map((entry) => (
                      <LeaderboardRow
                        key={entry.user_id}
                        entry={entry}
                        rank={entry.rank!}
                        isCurrentUser={entry.user_id === user?.id}
                        t={t}
                      />
                  ))}

                  {/* Preview rows for Section A (single member only) */}
                  {previewRowsA.map((entry) => (
                    <LeaderboardRow
                      key={entry.user_id}
                      entry={entry}
                      rank={entry.rank!}
                      isCurrentUser={false}
                      t={t}
                    />
                  ))}

                  {/* Red divider between sections */}
                  <div className="border-b-[3px] border-destructive my-2" />

                  {/* Section B: Users with >100 drinks */}
                  {sectionB.map((entry) => (
                      <LeaderboardRow
                        key={entry.user_id}
                        entry={entry}
                        rank={entry.rank!}
                        isCurrentUser={entry.user_id === user?.id}
                        t={t}
                      />
                  ))}

                  {/* Preview rows for Section B */}
                  {previewRowsB.map((entry) => (
                    <LeaderboardRow
                      key={entry.user_id}
                      entry={entry}
                      rank={entry.rank!}
                      isCurrentUser={false}
                      t={t}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center text-muted-foreground">
              {t('leaderboardNoParticipants')}
            </div>
          )}
        </div>
      </section>
    );
  }
);
